import { useCallback, useEffect, useRef, useState } from 'react';
import { useSettings } from '@wu/widget-core';
import type { Mode } from '../../types';
import { useA11yState } from './use-a11y-state';
import { useDemoToggle } from './use-demo-toggle';

type UseDemoToggleAnimationResult = {
  isBefore: boolean;
  isAfter: boolean;
  isAfterAnimating: boolean;
  afterAnimationRunId: number;
  handleBeforeClick(): void;
  handleAfterClick(): void;
  handleAfterAnimationEnd(): void;
};

export function useDemoToggleAnimation(): UseDemoToggleAnimationResult {
  const { editMode } = useSettings();
  const {
    a11yState: { mode }
  } = useA11yState();
  const { toggleState, setToggleState } = useDemoToggle();

  const previousModeRef = useRef<Mode | undefined>(mode);
  const animationFrameRef = useRef<number | null>(null);
  const [isAfterAnimating, setIsAfterAnimating] = useState(false);
  const [afterAnimationRunId, setAfterAnimationRunId] = useState(0);

  const clearAnimationFrame = useCallback(() => {
    if (animationFrameRef.current !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const runBeforeToAfterAnimation = useCallback(() => {
    clearAnimationFrame();
    setToggleState('before');
    setIsAfterAnimating(false);
    setAfterAnimationRunId((runId) => runId + 1);

    if (
      typeof window === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setToggleState('after');
      return;
    }

    animationFrameRef.current = window.requestAnimationFrame(() => {
      setIsAfterAnimating(true);
      animationFrameRef.current = null;
    });
  }, [clearAnimationFrame, setToggleState]);

  const handleBeforeClick = useCallback(() => {
    clearAnimationFrame();
    setIsAfterAnimating(false);
    setToggleState('before');
  }, [clearAnimationFrame, setToggleState]);

  const handleAfterClick = useCallback(() => {
    clearAnimationFrame();
    setIsAfterAnimating(false);
    setToggleState('after');
  }, [clearAnimationFrame, setToggleState]);

  const handleAfterAnimationEnd = useCallback(() => {
    if (!isAfterAnimating) {
      return;
    }

    setIsAfterAnimating(false);
    setToggleState('after');
  }, [isAfterAnimating, setToggleState]);

  useEffect(() => {
    runBeforeToAfterAnimation();
  }, [runBeforeToAfterAnimation]);

  useEffect(() => {
    const previousMode = previousModeRef.current;
    const shouldReplayAnimation =
      editMode &&
      previousMode !== undefined &&
      mode !== undefined &&
      previousMode !== mode;

    if (shouldReplayAnimation) {
      runBeforeToAfterAnimation();
    }

    previousModeRef.current = mode;
  }, [editMode, mode, runBeforeToAfterAnimation]);

  useEffect(() => clearAnimationFrame, [clearAnimationFrame]);

  return {
    isBefore: toggleState === 'before',
    isAfter: toggleState === 'after',
    isAfterAnimating,
    afterAnimationRunId,
    handleBeforeClick,
    handleAfterClick,
    handleAfterAnimationEnd
  };
}
